import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import Button from "ds/components/Button";
import Tooltip from "ds/components/Tooltip";
import useTypedContext from "hooks/useTypedContext";
import useConfirmRun from "shared/Run/useConfirmRun";
import { AccessResult } from "utils/access";

import { useRunShortcut } from "../../hooks/useRunShortcut";

type ConfirmRunButtonProps = {
  stackId: string;
  runId: string;
  isStackDisabled: boolean;
  canConfirm?: AccessResult;
};

const ConfirmRunButton = ({
  stackId,
  runId,
  isStackDisabled,
  canConfirm,
}: ConfirmRunButtonProps) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const [runConfirm, { loading, data }] = useConfirmRun({
    refetchQueries: ["GetRun"],
  });

  const onClick = useCallback(() => {
    return runConfirm({ stack: stackId, run: runId, note: null }, (data) => {
      if (data?.runConfirm?.id) {
        reportSuccess({ message: `Run ${data.runConfirm.id} successfully confirmed` });
      }
    });
  }, [reportSuccess, runConfirm, runId, stackId]);

  const isLoading = loading || !!data?.runConfirm?.id;

  useRunShortcut({
    label: "Confirm",
    code: "mod+alt+enter",
    callback: onClick,
    loading: isLoading,
    disabled: !canConfirm || !canConfirm.result || isStackDisabled,
  });

  if (!canConfirm || (!canConfirm.result && !canConfirm.message)) {
    return null;
  }

  return (
    <Tooltip
      on={(props) => (
        <Button
          {...props}
          disabled={isLoading || isStackDisabled || !canConfirm.result}
          loading={isLoading}
          onPress={onClick}
          id="confirm-button"
          variant="primary"
          size="small"
        >
          Confirm
        </Button>
      )}
      active={isStackDisabled || !canConfirm.result}
    >
      {canConfirm.message || "Stack is disabled"}
    </Tooltip>
  );
};

export default ConfirmRunButton;
